import React, {Component} from "react";
import Paper from '@material-ui/core/Paper';
import makeStyles from "@material-ui/core/styles/makeStyles";
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

const classes = makeStyles({
    table: {
        minWidth: 655,
    },
});

const cellStyle = {
    fontSize: '0.7rem',
    padding: '3px 4px',
    verticalAlign: 'top'
};

const labelStyle = {
    color: '#777',
    fontSize: '0.62rem',
    display: 'inline-block',
    minWidth: 42
};

function round(value, decimals) {
    if (value === null || value === undefined || isNaN(value)) {
        return "-";
    }

    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}

function boolText(value) {
    if (value === true) return "YES";
    if (value === false) return "NO";
    return "-";
}

function valueOrDash(value) {
    return value === null || value === undefined || value === "" ? "-" : value;
}

function getActiveColor(active) {
    return active ? "#028d41" : "#d61c02";
}

function getBooleanColor(value, badWhenTrue) {
    if (value === null || value === undefined) return "#777";
    return badWhenTrue
        ? value ? "#d61c02" : "#028d41"
        : value ? "#028d41" : "#d61c02";
}

function getBatteryColor(percent) {
    if (percent === null || percent === undefined) return "#777";
    if (percent < 25) return "#d61c02";
    if (percent < 50) return "#d98200";
    return "#028d41";
}

function getGpsColor(signalLevel) {
    if (!signalLevel) return "#777";

    if (String(signalLevel).includes("5")) return "#028d41";
    if (String(signalLevel).includes("4") || String(signalLevel).includes("3")) return "#d98200";

    return "#d61c02";
}

function headingToText(deg) {
    if (deg === null || deg === undefined || isNaN(deg)) return "-";

    const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    return dirs[Math.round(deg / 45) % 8];
}

function ageSeconds(timestampMs) {
    if (!timestampMs) return null;
    return (Date.now() - timestampMs) / 1000;
}

function getAgeColor(age) {
    if (age === null || age === undefined) return "#777";
    if (age < 2) return "#028d41";
    if (age < 5) return "#d98200";
    return "#d61c02";
}

function MetricLine({label, value, color}) {
    return (
        <div>
            <span style={labelStyle}>{label}</span>
            <span style={{color: color || "inherit"}}>{value}</span>
        </div>
    );
}

class LiveTable extends Component {

    state = {
        page: 0,
        rowsPerPage: 4
    };

    render() {
        const {dronesLive} = this.props;

        return (
            <Paper>
                <TableContainer>
                    <Table className={classes.table} size="small" aria-label="drone telemetry table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={cellStyle} align="center">Drone</TableCell>
                                <TableCell style={cellStyle} align="center">Status</TableCell>
                                <TableCell style={cellStyle} align="center">Flight</TableCell>
                                <TableCell style={cellStyle} align="center">Position</TableCell>
                                <TableCell style={cellStyle} align="center">Attitude</TableCell>
                                <TableCell style={cellStyle} align="center">Speed</TableCell>
                                <TableCell style={cellStyle} align="center">GPS / Compass</TableCell>
                                <TableCell style={cellStyle} align="center">Battery</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {Object.keys(dronesLive || {})
                                .slice(
                                    this.state.page * this.state.rowsPerPage,
                                    this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
                                )
                                .map(serial => {
                                    const drone = dronesLive[serial] || {};
                                    const telemetry = drone.latestTelemetry || {};
                                    const location = telemetry.location || {};
                                    const attitude = telemetry.attitude || {};
                                    const velocity = telemetry.velocity || {};
                                    const gps = telemetry.gps || {};
                                    const battery = telemetry.battery || {};

                                    const horizontalSpeed =
                                        velocity.x !== undefined && velocity.y !== undefined
                                            ? Math.sqrt(Math.pow(velocity.x, 2) + Math.pow(velocity.y, 2))
                                            : null;

                                    const age = ageSeconds(telemetry.timestampMs);

                                    return (
                                        <TableRow key={serial}>
                                            <TableCell style={cellStyle} align="center">
                                                <div style={{fontWeight: 600}}>
                                                    {drone.droneName || serial}
                                                </div>
                                                <div style={{color: '#777'}}>
                                                    {drone.droneModel || "-"}
                                                </div>
                                                <div style={{color: '#999', fontSize: '0.62rem'}}>
                                                    {serial}
                                                </div>
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <div
                                                    style={{
                                                        backgroundColor: getActiveColor(drone.active),
                                                        color: "#FFF",
                                                        borderRadius: 4,
                                                        padding: "2px 4px",
                                                        marginBottom: 3,
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {drone.active ? "ACTIVE" : "INACTIVE"}
                                                </div>

                                                <MetricLine
                                                    label="Conn"
                                                    value={boolText(telemetry.connectionStatus)}
                                                    color={getBooleanColor(telemetry.connectionStatus, false)}
                                                />

                                                <MetricLine
                                                    label="Safe"
                                                    value={boolText(telemetry.isFailSafe)}
                                                    color={getBooleanColor(telemetry.isFailSafe, true)}
                                                />

                                                <MetricLine
                                                    label="Last"
                                                    value={age !== null ? `${round(age, 1)}s` : "-"}
                                                    color={getAgeColor(age)}
                                                />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine label="Flying" value={boolText(telemetry.isFlying)} />
                                                <MetricLine label="Mode" value={valueOrDash(telemetry.flightMode)} />
                                                <MetricLine label="RTH" value={valueOrDash(telemetry.goHomeState)} />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine label="Lat" value={round(location.lat, 5)} />
                                                <MetricLine label="Lon" value={round(location.lon, 5)} />
                                                <MetricLine label="Alt" value={`${round(location.altitude, 1)} m`} />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine label="Pitch" value={`${round(attitude.pitch, 1)}°`} />
                                                <MetricLine label="Roll" value={`${round(attitude.roll, 1)}°`} />
                                                <MetricLine label="Yaw" value={`${round(attitude.yaw, 1)}°`} />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine
                                                    label="Horz"
                                                    value={horizontalSpeed !== null ? `${round(horizontalSpeed, 2)} m/s` : "-"}
                                                />
                                                <MetricLine
                                                    label="Climb"
                                                    value={velocity.z !== undefined && velocity.z !== null
                                                        ? `${round(velocity.z, 2)} m/s`
                                                        : "-"}
                                                />
                                                <MetricLine label="X" value={round(velocity.x, 2)} />
                                                <MetricLine label="Y" value={round(velocity.y, 2)} />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine label="Sat" value={valueOrDash(gps.satellites)} />
                                                <MetricLine
                                                    label="Signal"
                                                    value={valueOrDash(gps.signalLevel)}
                                                    color={getGpsColor(gps.signalLevel)}
                                                />
                                                <MetricLine
                                                    label="Head"
                                                    value={`${round(telemetry.compassHeading, 1)}° (${headingToText(telemetry.compassHeading)})`}
                                                />
                                                <MetricLine
                                                    label="Err"
                                                    value={boolText(telemetry.compassHasError)}
                                                    color={getBooleanColor(telemetry.compassHasError, true)}
                                                />
                                            </TableCell>

                                            <TableCell style={cellStyle} align="center">
                                                <MetricLine
                                                    label="Charge"
                                                    value={battery.percent !== undefined && battery.percent !== null
                                                        ? `${battery.percent}%`
                                                        : "-"}
                                                    color={getBatteryColor(battery.percent)}
                                                />
                                                <MetricLine
                                                    label="Volt"
                                                    value={battery.voltageMv !== undefined && battery.voltageMv !== null
                                                        ? `${battery.voltageMv} mV`
                                                        : "-"}
                                                />
                                                <MetricLine
                                                    label="Curr"
                                                    value={battery.currentMa !== undefined && battery.currentMa !== null
                                                        ? `${battery.currentMa} mA`
                                                        : "-"}
                                                />
                                                <MetricLine
                                                    label="Temp"
                                                    value={battery.temperatureC !== undefined && battery.temperatureC !== null
                                                        ? `${round(battery.temperatureC, 1)}°C`
                                                        : "-"}
                                                    color={battery.temperatureC > 60 ? "#d61c02" : undefined}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    }
}

export default LiveTable;